/* eslint-disable quotes */
import { deleteOne, getAll, getOne, patch, post, put } from './CommonServices/httpService'
import { useTokenClaims } from '../security/useTokenClaims'
import { Employee, SmallWorker } from '../types/Employee/Employee'
import { showErrorToast } from './CommonServices/alertServices'
import { ThrownServiceError } from '../types/Core/ThrownServiceError'

const unassignWorker = async (employee: Employee, fromSupervisor?: Employee) => {
	const result = await deleteOne(
		`/Workers/${employee.id}/Supervisor${
			employee.role.title === 'QualityControl' ? `?fromSupervisor=${fromSupervisor?.id}` : ''
		}`,
		`Could not unassign worker ${employee.firstName} ${employee.lastName}.`,
	)
	return result.success
}

const assignWorker = async (employeeFrom: Employee, employeeTo?: Employee) => {
	try {
		if (!employeeTo)
			throw { eventId: 1, traceId: 'AW001', message: 'EmployeeTo cannot be undefined in assign' }
		if (employeeTo.id === employeeFrom.id) throw { message: 'Cannot assign to same employee' }
		if (employeeFrom.role.hierarchyIndex <= employeeTo.role.hierarchyIndex)
			throw { message: `Cannot assign ${employeeFrom.role.title} to ${employeeTo.role.title}` }
		if (
			employeeTo.directReports?.some((w) => w.role.title === 'QualityControl') &&
			employeeFrom.role.title === 'QualityControl'
		)
			throw { message: `Cannot assign multiple QCs to one supervisor.` }

		const result = await post(`/Workers/${employeeTo.id}/DirectReports/${employeeFrom.id}`, {})
		return result.success
	} catch (err: any) {
		throw {
			message: err.message
				? err.message
				: `Could not assign worker ${employeeFrom.firstName} ${employeeFrom.lastName} to ${
						employeeTo?.firstName ?? ''
				  } ${employeeTo?.lastName ?? ''}`,
		}
	}
}

const getAssignWorkersData = async (): Promise<{
	assignedWorkers: Employee[]
	availableWorkers: Employee[]
}> => {
	try {
		//This service is only for PCM no need to filter for all roles
		const { id, role } = useTokenClaims()

		const workerPool = await getAll<Employee[]>(
			`/Workers?${
				role.title === 'ProjectControls'
					? 'includeChildren=true'
					: `reportsTo=${id}&includeChildren=true`
			}`,
			'Could not retrieve worker data for PCM.',
		)

		// split up immediate children when not the next level below ProjectControls
		// they go into Left Side, otherwise all direct reports
		const leftSide = workerPool.data.filter((w: Employee) => {
			return w.role.title !== 'ProjectControls'
		})
		const rightSide = workerPool.data
			.filter((w: Employee) => {
				return w.role.hierarchyIndex === 2
			})
			?.flatMap((w) => w.directReports ?? [])

		return { availableWorkers: leftSide, assignedWorkers: rightSide }
	} catch (err) {
		const { eventId, message } = err as ThrownServiceError
		showErrorToast({
			eventId: eventId ?? '',
			traceId: 'projectService',
			message: message ?? '',
		})
		return { availableWorkers: [], assignedWorkers: [] }
	}
}

const getForemenOfProject = async (gfId: number | undefined = undefined) => {
	try {
		const result = await getAll<Employee[]>(`/Workers/Formans${gfId ? `?gfId=${gfId}` : ''}`)
		return result.data
	} catch (err) {
		const { eventId, message } = err as ThrownServiceError
		showErrorToast({
			eventId: eventId ?? '',
			message: message ?? 'Could not retrieve foremen for this project',
		})
		return []
	}
}

const getLaborsOfForeman = async (foremanId: number) => {
	try {
		const result = await getAll<Employee[]>(`/Workers/Foreman/${foremanId}/Labors`)
		return result.data
	} catch (err) {
		const { eventId, message } = err as ThrownServiceError
		showErrorToast({
			eventId: eventId ?? '',
			message: message ?? 'Could not retrieve labors of this foreman',
		})
		return null
	}
}

const AssignWorkersToForman = async (
	fromFormanId: number,
	toFormanId: number,
	workerIds: number[],
	passToGf: boolean,
) => {
	try {
		const result = await patch(
			`/Workers/${toFormanId}?passToGf=${passToGf}&fromFormanId=${fromFormanId}`,
			workerIds,
		)
		return result.success
	} catch (err) {
		const { eventId, message } = err as ThrownServiceError
		showErrorToast({
			eventId: eventId ?? '',
			message: message ?? `Could not assign workers to forman id ${toFormanId}`,
		})
		return false
	}
}

const getWorkerExtraInfo = async (workerId: number) => {
	try {
		const result = await getOne<Employee>(`/Workers/${workerId}/info`)
		return result.data
	} catch (err) {
		const { eventId, message } = err as ThrownServiceError
		showErrorToast({
			eventId: eventId ?? '',
			message: message ?? `Could not fetch worker's info: ${workerId}`,
		})
		return null
	}
}

const updateWorkerInfo = async (worker: Employee) => {
	try {
		const result = await put(`/Workers`, worker)
		return result.success
	} catch (err) {
		const { eventId, message } = err as ThrownServiceError
		showErrorToast({
			eventId: eventId ?? '',
			message: message ?? `Could not update ${worker.firstName} ${worker.lastName}'s info`,
		})
		return null
	}
}

const removeWorker = async (worker: Employee) => {
	try {
		const result = await deleteOne(`/Workers/${worker.id}`)
		return result.success
	} catch (err) {
		const { eventId, message } = err as ThrownServiceError
		showErrorToast({
			eventId: eventId ?? '',
			message: message ?? `Could not remove ${worker.firstName} ${worker.lastName}`,
		})
		return null
	}
}

const reactivateWorker = async (worker: Employee) => {
	const result = await patch(`/Workers/Reactivate/${worker.id}`, {})
	return result.success
}

const getWorkersOfRole = async (roleIndex: number) => {
	const result = await getAll<SmallWorker[]>(`/Workers/Role/${roleIndex}`)
	return result.data
}
const getSmallDirectReports = async (reportsTo: number) => {
	const result = await getAll<SmallWorker[]>(`/Workers/DirectReports/${reportsTo}/Small`)
	return result.data
}
const TransferToSupervisor = async (transferData: {
	toSupervisorId: number
	fromSupervisorId: number
	directReports: {
		id: number
		transferSubordinates: boolean
		transferWorkItems: boolean
		subordinatesTransferMap: Record<number, number>
	}[]
}) => {
	// Send the entire transfer data in one request
	const result = await patch(`/Workers/Transfer`, transferData)
	return result.success
}

const addNewWorker = async (worker: Employee) => {
	const result = await post(`/Workers`, worker)
	return result.success
}

export {
	unassignWorker,
	getAssignWorkersData,
	assignWorker,
	getForemenOfProject,
	AssignWorkersToForman,
	getWorkerExtraInfo,
	updateWorkerInfo,
	getLaborsOfForeman,
	removeWorker,
	getWorkersOfRole,
	getSmallDirectReports,
	TransferToSupervisor,
	reactivateWorker,
	addNewWorker,
}
